"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, PhoneCall, ShieldCheck, Wind } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface RelatedServiceItem {
  title: string;
  description: string;
  to: string;
  tag: string;
}
const relatedServices: RelatedServiceItem[] = [{
  title: "Water Damage & Flood Restoration",
  description: "Rapid water extraction, industrial structural drying, and moisture mapping to stop mold before it begins.",
  to: "/WaterDamageFloodRestorationPage",
  tag: "Immediate Extraction"
}, {
  title: "Reconstruction & Design-Build",
  description: "Full-scope structural repairs, drywall replacement, and finish carpentry once remediation clearance is certified.",
  to: "/ReconstructionDesignBuildPage",
  tag: "Turn-Key Rebuild"
}, {
  title: "Asbestos & Lead Abatement",
  description: "Licensed containment, regulated material removal, and certified hazardous waste disposal across the Mid-Atlantic.",
  to: "/AsbestosLeadAbatementPage",
  tag: "Certified Abatement"
}];
const containmentSpecs = ["true negative-pressure HEPA filtration prevents spore migration", "Critical poly barriers seal off unaffected living and working spaces", "Third-party post-remediation clearance testing before containment removal"];
export default function WhyChoose() {
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-8 mb-12 border-b border-border/40 text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            <span>24/7 Field Dispatch • MD / DC / NOVA</span>
          </div>
          <div className="hidden sm:flex items-center gap-6">
            <span>Direct Technician Dispatch</span>
            <span className="text-border">•</span>
            <span>No Call Centers</span>
            <span className="text-border">•</span>
            <span>Insurance Direct Billing</span>
          </div>
        </div>

        {/* Featured Containment Protocol Panel */}
        <div className="bg-secondary text-secondary-foreground rounded-2xl border border-border/20 p-6 sm:p-8 lg:p-12 mb-16 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Field Image with Equipment Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }} className="lg:col-span-6 relative">
              <div className="relative overflow-hidden rounded-xl border border-border/30 shadow-lg bg-card/10">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/containment_barrier_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/containment_barrier_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/containment_barrier_technician.png-wvc-sizes" alt="TriServe restoration technician sealing a negative pressure containment barrier" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                
                {/* Tech Spec Inset Badge */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3 bg-card/90 backdrop-blur-xs text-card-foreground px-4 py-2.5 rounded-lg border border-border/40 shadow-sm">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Wind className="size-4 text-primary shrink-0" />
                    <span className="text-xs font-mono font-medium truncate">
                      Negative Air Machine • 2.0 In-W.C. Sealed
                    </span>
                  </div>
                  <span className="inline-flex items-center text-[11px] font-semibold uppercase px-2 py-0.5 rounded-sm bg-primary/15 text-primary shrink-0">
                    Active
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Protocol Copy & Direct Actions */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.1,
            ease: "easeOut"
          }} className="lg:col-span-6 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 self-start rounded-md bg-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent mb-4">
                <ShieldCheck className="size-4" />
                <span>Strict Protocol Containment</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight leading-tight mb-4">
                Local containment crews, verified safe before handover
              </h2>

              <p className="text-base sm:text-lg text-secondary-foreground/90 leading-relaxed mb-6">
                Our technicians build airtight containment chambers with physical poly barriers and commercial HEPA air scrubbers. By establishing continuous negative pressure, we isolate micro-pollutants and mold spores at the source, ensuring airborne contaminants never drift into unaffected rooms or ventilation ducts.
              </p>

              <div className="space-y-3 mb-8">
                {containmentSpecs.map((spec, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-secondary-foreground/90 font-medium">
                      {spec}
                    </span>
                  </div>)}
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-md h-11 px-6 rounded-md">
                  <Link to="/ContactRequestServicePage">
                    Request an Inspection
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-11 px-5 border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent">
                  <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                    <PhoneCall className="size-4 text-accent" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Related Services Section Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">
                Coordinated Mitigation &amp; Recovery
              </p>
              <h3 className="text-xl sm:text-2xl font-bold font-default tracking-tight">
                Related disaster recovery services
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Disasters rarely happen in isolation. Our local teams handle structural drying, abatement, and repairs under a single coordinated scope.
            </p>
          </div>
        </div>

        {/* 3 Related Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {relatedServices.map((service, i) => <motion.div key={service.title} data-index={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: i * 0.08,
          ease: "easeOut"
        }}>
              <Card className="h-full bg-card text-card-foreground border border-border flex flex-col justify-between hover:border-primary/50 transition-[border-color,box-shadow] duration-200 hover:shadow-md">
                <CardHeader className="space-y-3 pb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary font-semibold px-2.5 py-1 rounded bg-primary/10">
                      {service.tag}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">
                      0{i + 1}
                    </span>
                  </div>
                  <CardTitle className="text-lg font-bold font-default leading-snug">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button asChild variant="ghost" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent group inline-flex items-center gap-1.5">
                    <Link to={service.to}>
                      <span>Explore Service Details</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}